import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  isBootSplashFinished,
  subscribeBootSplashFinished,
} from 'pages/components/Loader/BootSplash';
import {
  DEFAULT_LOADER_MESSAGE,
  getRouteLoadingMessage,
} from 'pages/components/Loader/getRouteLoadingMessage';
import * as S from './Loader.styled';

type LoaderProps = {
  isSticky?: true;
  skipSideBar?: true;
  isNoBg?: true;
  /** Skip fade-in so a suspending route does not flash white first. */
  instant?: boolean;
  /** Page name or process, e.g. "Loading rooms…". Falls back to the URL. */
  message?: string;
};

/** Spinner + label card; use inside an in-page slot (no overlay). */
export const LoaderCard = ({
  message,
  inPage,
  compact,
}: {
  message: string,
  inPage?: boolean,
  compact?: boolean,
}) => (
  <S.Progress
    $inPage={inPage}
    $compact={compact}
    role="status"
    aria-live="polite"
    aria-label={message}
  >
    <S.SpinnerWrap>
      <S.Spinner $compact={compact} aria-hidden />
    </S.SpinnerWrap>
    <S.Message $compact={compact}>{message}</S.Message>
  </S.Progress>
);

/**
 * In-app overlay loader (actions / page data).
 * Never shown while the refresh logo splash is active.
 * Always shows a label — page message, else second URL segment, else "Loading…".
 */
const Loader = function Loader({
  isSticky,
  isNoBg,
  instant,
  message,
}: LoaderProps) {
  const { pathname } = useLocation();
  const [, setBootTick] = useState(0);

  useEffect(() => {
    if (isBootSplashFinished()) return undefined;
    return subscribeBootSplashFinished(() => setBootTick((n) => n + 1));
  }, []);

  if (!isBootSplashFinished()) return null;

  const label = (message && message.trim()) || getRouteLoadingMessage(pathname)
    || DEFAULT_LOADER_MESSAGE;

  const overlay = (
    <S.Root $instant={instant}>
      {isNoBg ? null : <S.Bg isSticky={isSticky} />}
      <LoaderCard message={label} />
    </S.Root>
  );

  return createPortal(overlay, document.body);
};

export default Loader;
